<template>
  <b-sidebar
    id="order-crew-panel"
    :visible="!!selected"
    right
    shadow
    backdrop
    width="420px"
    bg-variant="white"
    :title="order ? 'Ordre ' + order.ordernumber : ''"
    @hidden="$emit('close')"
  >
    <div v-if="order" class="px-3 pb-4">
      <p class="mb-1 text-muted">{{ order.title }}</p>
      <p class="mb-2">
        <b-badge pill variant="secondary" class="mr-1">{{ order.status }}</b-badge>
        <b-badge v-if="order.type" pill variant="light" class="mr-1">{{ order.type }}</b-badge>
        {{ fmt(order.startdate) }} – {{ fmt(order.enddate) }}
      </p>
      <p class="mb-3 small">
        <b-link :to="'/dashboard/order-edit/' + order.id">Åpne ordre</b-link>
        <span class="text-muted"> · {{ vessel.name }}</span>
      </p>

      <!-- Why the order is not planned automatically -->
      <b-alert v-if="!order.managed" show variant="info" class="small">
        <template v-if="order.unmanaged_reason === 'ingen_regel'">
          Fartøyet har ingen rotasjonsregel. Mannskapet vises slik det står på ordren.
        </template>
        <template v-else-if="order.unmanaged_reason === 'laast'">
          Mannskapet er låst på denne ordren og oppdateres ikke av planleggeren.
        </template>
        <template v-else>
          Ordren er avsluttet eller passert. Mannskapet endres ikke.
        </template>
      </b-alert>

      <!-- Warnings -->
      <b-alert
        v-for="(w, i) in order.warnings"
        :key="'w' + i"
        show
        variant="warning"
        class="small py-2 mb-2"
      >
        <i class="ion ion-ios-warning mr-1"></i>{{ w.text }}
      </b-alert>

      <!-- Skift -->
      <b-form-group label="Skift" label-class="font-weight-bold" class="mt-3">
        <b-form-select
          :value="order.skift_manual ? order.skift : null"
          :options="skiftOptions"
          :disabled="!order.managed || saving"
          @change="setSkift"
        />
        <small v-if="!order.skift_manual && order.skift_name && order.managed" class="text-muted">
          Etter regel: {{ order.skift_name }}
        </small>
      </b-form-group>

      <!-- Crew -->
      <div class="d-flex align-items-center mb-2 mt-4">
        <h6 class="font-weight-bold mb-0 flex-grow-1">Mannskap ({{ order.crew.length }})</h6>
        <b-button
          v-if="order.managed"
          size="sm"
          variant="outline-primary"
          @click="$emit('change-crew', null)"
        ><i class="ion ion-ios-add mr-1"></i>Legg til</b-button>
      </div>

      <div
        v-for="c in sortedCrew"
        :key="c.id"
        class="cp-member border rounded p-2 mb-2 d-flex align-items-center"
        :class="{ 'cp-member-in': c.source === 'innbytter' }"
      >
        <div class="flex-grow-1">
          <div>
            <i v-if="c.id === order.captain" class="ion ion-ios-star text-warning mr-1" title="Skipper på ordren"></i>
            {{ c.name }}
          </div>
          <div class="small">
            <b-badge pill :variant="roleVariant(c.role)" class="mr-1">{{ c.role || "Ukjent" }}</b-badge>
            <b-badge v-if="c.source === 'innbytter'" pill variant="success">Innbytter</b-badge>
          </div>
        </div>
        <b-button
          v-if="order.managed"
          size="sm"
          variant="link"
          class="text-danger p-0"
          @click="$emit('change-crew', c)"
        >Kan ikke delta</b-button>
      </div>
      <p v-if="!order.crew.length" class="text-muted small">Ingen mannskap på ordren.</p>

      <!-- Removed from the base skift -->
      <template v-if="order.removed.length">
        <h6 class="font-weight-bold mt-4">Ute fra skiftet</h6>
        <div v-for="r in order.removed" :key="r.id + r.change" class="small mb-1 d-flex align-items-center">
          <span class="cp-removed flex-grow-1">{{ r.name }} <span class="text-muted">({{ r.role }})</span></span>
          <b-badge pill variant="light">{{ reasonLabel(r.reason) }}</b-badge>
        </div>
      </template>

      <!-- Changes that affect this order -->
      <template v-if="orderChanges.length">
        <h6 class="font-weight-bold mt-4">Endringer</h6>
        <div v-for="ch in orderChanges" :key="ch.id" class="border rounded p-2 mb-2 small">
          <div class="d-flex">
            <div class="flex-grow-1">
              <span v-if="ch.crew_out">{{ ch.crew_out.name }} ut</span>
              <span v-if="ch.crew_out && ch.crew_in"> → </span>
              <span v-if="ch.crew_in">{{ ch.crew_in.name }} inn</span>
            </div>
            <b-badge pill variant="light">{{ reasonLabel(ch.reason) }}</b-badge>
          </div>
          <div class="text-muted">
            {{ ch.order ? "Kun denne ordren" : "Periode " + fmt(ch.from_date) + " – " + fmt(ch.to_date) }}
            <span v-if="ch.note"> · {{ ch.note }}</span>
          </div>
          <b-button size="sm" variant="link" class="p-0 text-danger" :disabled="saving" @click="removeChange(ch)">
            Angre endring
          </b-button>
        </div>
      </template>

      <!-- Lock -->
      <hr class="mt-4" />
      <b-form-checkbox
        class="ml-4"
        switch
        :checked="order.crew_locked"
        :disabled="saving || (!order.managed && order.unmanaged_reason !== 'laast')"
        @change="setLocked"
      >
        Lås mannskap på ordren
      </b-form-checkbox>
      <small class="text-muted d-block mt-1">
        Låst mannskap oppdateres ikke av planleggeren. Bruk dette når mannskapet er satt manuelt på ordren.
      </small>
    </div>
  </b-sidebar>
</template>

<script>
import dayjs from "dayjs";
import api, { reasonLabel, errorText } from "./api";

const ROLE_VARIANTS = {
  Skipper: "danger",
  Styrmann: "warning",
  Båtfører: "success",
  Maskinist: "primary",
  Motormann: "dark",
  Matros: "info",
  Dekksmann: "secondary",
  Kokk: "warning",
  Operatør: "primary",
  Maskinpasser: "dark",
  "Matros/Kokk": "info",
  Flåteansvarlig: "success",
};

export default {
  name: "OrderCrewPanel",
  props: {
    selected: { type: Object, default: null }, // { vessel, order }
    plan: { type: Object, default: null },
  },
  data() {
    return { saving: false };
  },
  computed: {
    order() {
      return this.selected && this.selected.order;
    },
    vessel() {
      return this.selected && this.selected.vessel;
    },
    skiftOptions() {
      const skifts = (this.plan && this.plan.skifts) || [];
      const ruleIds = (this.vessel && this.vessel.rule && this.vessel.rule.skifts) || [];
      const forVessel = skifts.filter((s) => ruleIds.includes(s.id) || s.vessel === (this.vessel && this.vessel.id));
      const auto =
        this.vessel && this.vessel.rule && this.vessel.rule.mode !== "manuell"
          ? "Automatisk etter regel"
          : "Ingen skift valgt";
      return [{ value: null, text: auto }].concat(forVessel.map((s) => ({ value: s.id, text: s.name })));
    },
    sortedCrew() {
      return this.order.crew.slice().sort((a, b) => {
        if (a.id === this.order.captain) return -1;
        if (b.id === this.order.captain) return 1;
        return String(a.name).localeCompare(String(b.name), "nb");
      });
    },
    orderChanges() {
      const ids = (this.order && this.order.changes) || [];
      return ((this.plan && this.plan.changes) || []).filter((c) => ids.includes(c.id));
    },
  },
  methods: {
    reasonLabel,
    roleVariant(role) {
      return ROLE_VARIANTS[role] || "light";
    },
    fmt(date) {
      return date ? dayjs(date).format("DD.MM.YYYY") : "";
    },
    async run(fn, success) {
      this.saving = true;
      try {
        await fn();
        if (success) this.toast(success, "success");
        this.$emit("changed");
      } catch (error) {
        console.log(error);
        this.toast(errorText(error), "danger");
      } finally {
        this.saving = false;
      }
    },
    setSkift(skiftId) {
      // null = back to the rule (or "no skift" in manual mode).
      const fields = skiftId
        ? { skift: skiftId, skift_manual: true }
        : { skift_manual: false, ...(this.vessel.rule && this.vessel.rule.mode === "manuell" ? { skift: null } : {}) };
      this.run(() => api.updateOrder(this.order.id, fields), "Skift oppdatert");
    },
    setLocked(locked) {
      this.run(
        () => api.updateOrder(this.order.id, { crew_locked: locked }),
        locked ? "Mannskapet er låst" : "Mannskapet følger planen igjen"
      );
    },
    removeChange(change) {
      this.$bvModal
        .msgBoxConfirm("Angre denne endringen? Mannskapet regnes ut på nytt for alle ordrer den gjelder.", {
          title: "Angre endring",
          okTitle: "Angre endring",
          cancelTitle: "Avbryt",
          okVariant: "danger",
        })
        .then((ok) => ok && this.run(() => api.deleteChange(change.id), "Endringen er fjernet"));
    },
    toast(text, variant) {
      this.$bvToast.toast(text, {
        title: variant === "danger" ? "Feil" : "Lagret",
        variant,
        solid: true,
        autoHideDelay: 2500,
      });
    },
  },
};
</script>

<style lang="css">
.cp-member {
  background: #f8f9fa;
}
.cp-member-in {
  background: #eaf6ec;
  border-color: #28a745 !important;
}
.cp-removed {
  text-decoration: line-through;
  color: #6c757d;
}
</style>
